import React, { useState, useContext, useEffect, Fragment } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { NavBar, Tabs, List, Tag, Ellipsis, Badge, Skeleton, ErrorBlock } from 'antd-mobile';
import styled from 'styled-components';
import { getWorkOrder, getWorkOrderStats } from '../api';
import { getWorkOrderStatusData } from '../utils/helpers';
import { WORK_ORDER_TYPE_AIRCRAFT } from '../utils/constants';
import Tasks from './Tasks';
import Defects from './Defects';
import Materials from './Materials';
import Files from './Files';
import Notes from './Notes';
import { featureEnabled, matchesPermission } from '../utils/permission-helpers';
import Tools from './Tools';
import WorkTimeToggle from '../components/WorkTimeToggle';
import UserSelfContext from '../utils/self-context';
import AccessPanels from './AccessPanels';

const WorkOrderWrapper = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-flow: column;
`;

const ContentWrapper = styled.div`
	flex: 1;
	min-height: 0;
	overflow:  auto;
	width: 100%;
	background: #f0f2f5;
`;

const TitleWrapper = styled.div`
	display: flex;
	align-items: center;
	width: calc(100vw - 24px);
`;

const StatusWrapper = styled.div`
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	font-size: var(--adm-font-size-5);
`;

const Title = styled.h2`
	display: inline-block;
	color: var(--adm-color-text);
	flex: 1;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

const TitleActions = styled.div`
	margin-left: auto;
	display: flex;
	align-items: center;
`;

const Status = styled(Tag)`
	margin-left: 8px;
`;

const WorkOrderInfo = styled.div`
	padding: 0px 12px;
	background: #fff;

	.adm-list-item-title {
		margin-bottom: 8px;
		display: flex;
		align-items: center;
	}

	h2 {
		margin: 0px 12px 0px 0px;
		display: inline-block;
		color: var(--adm-color-text);
	}

	.adm-ellipsis {
		a {
			margin-left: 8px;
		}
	}
`;

const NotFound = styled(ErrorBlock)`
	height: 100%;
	padding: 12px;
	justify-content: center;
	align-items: center;
	display: flex;
	flex-direction: column;
`;

const Nav = styled(NavBar)`
	background: #fff;
	display: flex;
	border-bottom: solid 1px var(--adm-color-border);
`;

const TabBar = styled(Tabs)`
	display: flex;
	flex-direction: column;
	height: 100%;
	.adm-tabs-header {
		background: #fff;
	}

	.adm-tabs-content {
		flex: 1;
		overflow:  auto;
		padding: 0px;
	}
`;

function WorkOrder() {
	const params = useParams();
	const history = useHistory();
	const { id } = params;
	const { userSelf } = useContext(UserSelfContext);
	const [loading, setLoading] = useState(false);
	const [err, setErr] = useState(false);
	const [workOrder, setWorkOrder] = useState({});
	const [stats, setStats] = useState([]);

	async function fetchWorkOrder(silent) {
		if (!silent) {
			setLoading(true);
		}
		try {
			const data = await getWorkOrder(id);
			setWorkOrder(data);
		} catch (e) {
			setErr(true);
		}
		setLoading(false);
	}

	async function fetchWorkOrdeStats() {
		setLoading(true);
		try {
			const data = await getWorkOrderStats(id);
			setStats(data);
		} catch (e) {
			setErr(true);
		}

		setLoading(false);
	}

	useEffect(() => {
		fetchWorkOrder();
		fetchWorkOrdeStats();
	}, []);

	function getUndoneDefects() {
		const allTasks = stats.filter(s => s.type === 'defect');
		const doneTasks = allTasks.filter(s => s.all_done).length;

		return allTasks.length - doneTasks;
	}

	function getUndoneOps() {
		const allTasks = stats.filter(s => s.type === 'work_order_op');
		const doneTasks = allTasks.filter(s => s.all_done).length;

		return allTasks.length - doneTasks;
	}

	function getUndoneAccessPanels() {
		const allTasks = stats.filter(s => s.type === 'access_panel');
		const doneTasks = allTasks.filter(s => s.all_done).length;

		return allTasks.length - doneTasks;
	}

	return (
		err ? <NotFound
			title="Work order not found"
			description="This work order does not exist, or you dont have rights to view it"
			status='empty'
		/> : <WorkOrderWrapper>
			<Nav onBack={() => history.push('/')} back={''}>
				Work Order
			</Nav>
			<WorkOrderInfo>
				{loading ?
					<Fragment>
						<Skeleton.Title animated/>
						<Skeleton.Paragraph lineCount={1} animated/>
					</Fragment> :
					<List.Item
						key={workOrder.id}
						title={<TitleWrapper>
							<Title>{workOrder.nr || '-'}</Title>
							<TitleActions>
								<WorkTimeToggle parent={workOrder} parentType="work_order" />
							</TitleActions>
						</TitleWrapper>}
						description={<Ellipsis
							direction='end'
							rows={2} content={workOrder.description || '-'}
							expandText="Show"
							collapseText="Hide"
						/>}
					>
						<StatusWrapper>
							{workOrder.aircraft_registration || workOrder.item_description || '-'}
							<Status color={getWorkOrderStatusData(workOrder.status).color}>
								{getWorkOrderStatusData(workOrder.status).text}
							</Status>
						</StatusWrapper>
					</List.Item>}
			</WorkOrderInfo>
			<ContentWrapper>
				<TabBar defaultActiveKey="tasks">
					<Tabs.Tab
						title={<Badge content={getUndoneOps() || null} style={{ '--right': '-10px', '--top': '4px' }}>Tasks</Badge>}
						key='tasks'
					>
						<Tasks stats={stats} workOrderId={workOrder.id} />
					</Tabs.Tab>
					<Tabs.Tab title={<Badge content={getUndoneDefects() || null} style={{ '--right': '-10px', '--top': '4px' }}>Additional Work</Badge>} key='defects'>
						<Defects
							stats={stats}
							onChange={fetchWorkOrdeStats}
							isClosed={workOrder.closed}
							workOrderId={workOrder.id}
						/>
					</Tabs.Tab>
					{(
						featureEnabled(userSelf, 'access_panels_enabled') &&
						workOrder.type === WORK_ORDER_TYPE_AIRCRAFT &&
						stats.filter(s => s.type === 'access_panel').length > 0
					) &&
						<Tabs.Tab title={<Badge content={getUndoneAccessPanels() || null} style={{ '--right': '-10px', '--top': '4px' }}>Access Panels</Badge>} key='access-panels'>
							<AccessPanels stats={stats} workOrderId={workOrder.id} />
						</Tabs.Tab>
					}
					{featureEnabled(userSelf, 'warehouse_enabled') && matchesPermission(userSelf, 'view_work_order_materials') &&
						<Tabs.Tab title='Materials' key='materials'>
							<Materials workOrderId={workOrder.id} />
						</Tabs.Tab>
					}
					{featureEnabled(userSelf, 'tools_enabled') && matchesPermission(userSelf, 'view_work_order_tools') &&
						<Tabs.Tab title='Tools' key='tools'>
							<Tools
								workOrderId={workOrder.id}
							/>
						</Tabs.Tab>
					}
					<Tabs.Tab
						title={<Badge
							content={workOrder.files_count || null}
							style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
								Files
						</Badge>}
						key='files'
					>
						<Files
							objectType="work_order"
							objectId={workOrder.id}
							onChange={() => fetchWorkOrder(true)}
							disabled={!matchesPermission(userSelf, 'manage_files_notes_work_orders')}
						/>
					</Tabs.Tab>
					<Tabs.Tab
						title={<Badge
							content={workOrder.notes_count || null}
							style={{ '--color': 'var(--adm-color-primary)', '--right': '-10px', '--top': '4px' }}>
								Notes
						</Badge>}
						key='notes'
					>
						<Notes
							objectType="work_order"
							objectId={workOrder.id}
							onChange={() => fetchWorkOrder(true)}
							disabled={!matchesPermission(userSelf, 'manage_files_notes_work_orders')}
						/>
					</Tabs.Tab>
				</TabBar>
			</ContentWrapper>
		</WorkOrderWrapper>
	);
}

export default WorkOrder;
